import APIService from 'api/APIService'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { UnitActiveCustomer } from 'types/unit'
import { useSettings } from 'core/hooks/settings/useSettings'
import useCurrentTeam from 'core/hooks/team/useCurrentTeam'
import { useUserProfile } from 'core/hooks/data/useDataHook'

/**
 * Return type for the useWallet hook.
 */
interface UseWalletReturn {
  /** Indicates if wallet data is currently being fetched */
  isLoading: boolean
  /** Whether the user has an active Everpay wallet */
  hasWallet: boolean
  /** The Unit customer wallet data, if available */
  wallet?: UnitActiveCustomer
  /** The wallet balance in dollars (converted from cents) */
  balance: number
}

/**
 * Custom hook for managing Everlance Payments (Everpay) wallet functionality.
 *
 * This hook fetches wallet information from the Unit API and provides
 * wallet status and balance data for the current user's team.
 *
 * @param includeBalance - When true, fetches the wallet balance along with wallet data.
 *                         Defaults to false for performance optimization.
 * @returns An object containing wallet loading state, availability, data, and balance.
 *
 * @example
 * // Basic usage - just check if user has a wallet
 * const { hasWallet, isLoading } = useWallet();
 *
 * @example
 * // Fetch wallet with balance
 * const { wallet, balance, hasWallet } = useWallet(true);
 */
const useWallet = (includeBalance = false): UseWalletReturn => {
  const {
    data: {
      featureFlags: { ENABLE_EVERLANCE_PAYMENTS }
    }
  } = useSettings()
  const { data: currentUser } = useUserProfile()

  const { data: team } = useCurrentTeam()
  const team_token_id = team?.token_id

  const { data, isLoading } = useQuery({
    queryKey: ['unit', 'wallet'],
    queryFn: () =>
      APIService.unit.unitGetTeamCustomer({
        team_token_id: String(team_token_id),
        scope: 'read'
      }),
    enabled:
      ENABLE_EVERLANCE_PAYMENTS &&
      !!team_token_id &&
      currentUser?.team_has_everpay_enabled
  })

  const { data: walletWithBalance, isLoading: isLoadingWithBalance } = useQuery(
    {
      queryKey: ['unit', 'wallet', 'withBalance'],
      queryFn: () =>
        APIService.unit.unitGetCurrentUserActiveCustomer(includeBalance),
      enabled: includeBalance && currentUser?.team_has_everpay_enabled
    }
  )

  const balance = useMemo(() => {
    return walletWithBalance?.unit_account.balance ?? 0
  }, [walletWithBalance])

  if (ENABLE_EVERLANCE_PAYMENTS && data) {
    return {
      isLoading: isLoading || isLoadingWithBalance,
      hasWallet: !!currentUser?.team_has_everpay_enabled,
      wallet: data,
      balance: isNaN(balance) ? balance : balance / 100
    }
  }
  return {
    isLoading: isLoading || isLoadingWithBalance,
    hasWallet: false,
    wallet: undefined,
    balance: 0
  }
}

export default useWallet
